import beTwMerge from '@/utils/beTwMerge';
import Button from '@/components/homepage/Button/Button';
import DropdownButton from '@/components/homepage/DropdownButton/DropdownButton';
import { getAnchorId, getComponentMargin } from '@/utils/builderUtils';
import ResponsiveImage, { ResponsiveImageSet } from '@/utils/ResponsiveImage';
import RichText from '@/components/RichText/RichText';

const baseDropdownButtonStyle = `
  tw-basis-[calc(50%-8px)] tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis
`;

const baseButtonStyle = `
  tw-basis-[calc(50%-8px)] tw-max-w-none tw-whitespace-nowrap tw-text-ellipsis tw-overflow-hidden
`;

export default function SlimHeroBannerBuilder({
  background,
  imgAlt,
  title,
  description,
  textColor,
  mobileTextColor,
  actions,
  whiteTextBackground,
  desktopBoxCopyPosition,
  desktopCopyPosition,
  anchorLinkID,
  margin,
}) {
  const slicedActions = actions?.slice(0, 2).map(action => ({
    ...action,
    color: action.color === 'dark' ? 'dark-green' : 'white',
    mobileColor: action.mobileColor === 'dark' ? 'dark-green' : 'white',
  }));

  const copyBoxPositionClassnames = {
    left: 'lg:tw-justify-start',
    right: 'lg:tw-justify-end',
    center: 'lg:tw-justify-center',
  };
  const copyPositionClassnames = {
    left: 'lg:tw-items-start',
    center: 'lg:tw-items-center',
  };

  const desktopImgSrcs: ResponsiveImageSet = [
    { src: background?.desktop || '', size: { width: 1440, height: 350, aspectRatio: 1440 / 350 }, breakpoint: 1023 },
  ];
  const mobileImgSrcs: ResponsiveImageSet = [
    { src: background?.mobile || '', size: { width: 380, height: 244, aspectRatio: 380 / 244 }, breakpoint: 320 },
    { src: background?.mobile || '', size: { width: 768, height: 480, aspectRatio: 24 / 15 }, breakpoint: 320 },
  ];

  return (
    <div
      className={beTwMerge(
        'tw-mx-0 min-[1472px]:tw-w-full lg:tw-max-w-[1440px] lg:tw-mx-8 min-[1472px]:tw-mx-auto lg:tw-max-h-[350px]',
        getComponentMargin(margin)
      )}
      {...getAnchorId(anchorLinkID)}
    >
      <div className="tw-w-full">
        <div className="tw-mx-auto min-[1440px]:tw-w-full min-[1440px]:tw-h-full tw-flex tw-h-full tw-flex-col tw-relative">
          <div className="tw-w-full tw-aspect-[195/122] md:tw-aspect-[24/15] lg:tw-aspect-[688/175] lg:tw-max-h-[350px] tw-inline-block tw-overflow-hidden">
            <a
              href={slicedActions?.[0]?.href}
              className="lg:tw-hidden tw-w-full tw-inline-block tw-overflow-hidden tw-h-full"
            >
              <ResponsiveImage
                preload
                loading="eager"
                fetchpriority="high"
                className="tw-w-full tw-h-full tw-object-cover"
                sources={mobileImgSrcs}
                alt={imgAlt || title}
              />
            </a>
            <a
              href={slicedActions?.[0]?.href}
              className="tw-w-full lg:tw-inline-block tw-overflow-hidden tw-h-full tw-hidden"
            >
              <ResponsiveImage
                preload
                loading="eager"
                fetchpriority="high"
                className="tw-w-full tw-h-full tw-object-cover"
                sources={desktopImgSrcs}
                alt={imgAlt || title}
              />
            </a>
          </div>

          <div
            className={beTwMerge(
              `lg:tw-absolute lg:tw-top-0 lg:tw-left-0 lg:tw-bottom-0 lg:tw-right-0 tw-flex tw-pointer-events-none tw-h-fit tw-items-center tw-justify-center lg:tw-w-full lg:tw-h-full`,
              copyBoxPositionClassnames[desktopBoxCopyPosition]
            )}
          >
            <div
              className={beTwMerge(
                `tw-mx-4 lg:tw-max-w-[500px] md:tw-max-w-[539px] lg:tw-max-h-[250px] tw-pt-10 lg:tw-px-6 lg:tw-py-10 tw-flex tw-flex-col tw-gap-y-4 tw-justify-center`,
                whiteTextBackground && 'tw-bg-white tw-bg-opacity-80',
                copyPositionClassnames[desktopCopyPosition],
                textColor === 'white' ? 'lg:tw-text-white' : 'lg:tw-text-be-dark-gray',
                mobileTextColor === 'white' ? 'tw-text-white' : 'tw-text-be-dark-gray'
              )}
            >
              {title && (
                <h1
                  className={beTwMerge(
                    'tw-text-5xl tw-text-center tw-font-[400] tw-m-0',
                    desktopCopyPosition === 'left' ? 'lg:tw-text-left' : 'tw-text-center'
                  )}
                >
                  {title}
                </h1>
              )}

              {description && (
                <div>
                  <RichText
                    className={beTwMerge(
                      'tw-text-inherit  tw-text-center tw-pointer-events-auto [&>p>a]:tw-text-inherit',
                      (desktopCopyPosition as string) === 'left' ? 'lg:tw-text-left' : 'tw-text-center'
                    )}
                    content={description}
                  />
                </div>
              )}

              {slicedActions?.length > 0 && (
                <div
                  className={beTwMerge(
                    'tw-pointer-events-auto tw-flex tw-items-center tw-justify-center lg:tw-justify-between md:tw-flex-row tw-flex-col tw-flex-wrap md:tw-gap-x-[16px] tw-gap-y-2 lg:tw-gap-y-4 tw-gap-x-0 lg:tw-w-full',
                    desktopCopyPosition === 'center' ? 'lg:tw-justify-center' : ''
                  )}
                >
                  {slicedActions?.slice(0, 2).map((action, i) => {
                    const { label, color, variant, mobileColor, mobileVariant } = action;

                    if (action.actions) {
                      const { actions: subActions } = action;
                      return (
                        <>
                          <DropdownButton
                            key={i}
                            label={label}
                            options={subActions}
                            color={color || mobileColor}
                            type={variant || mobileVariant}
                            className={beTwMerge(
                              baseDropdownButtonStyle,
                              '!tw-hidden lg:!tw-block',
                              actions.length > 1 ? 'tw-flex-1' : 'tw-flex-1 lg:tw-flex-[0_0_calc(50%_-_0.5rem)]'
                            )}
                          />

                          <DropdownButton
                            key={i}
                            label={label}
                            options={subActions}
                            color={mobileColor || color}
                            type={mobileVariant || variant}
                            className={beTwMerge(
                              baseDropdownButtonStyle,
                              '!tw-block tw-w-full lg:!tw-hidden',
                              actions.length > 1 ? 'tw-flex-1' : 'tw-flex-1 lg:tw-flex-[0_0_calc(50%_-_0.5rem)]'
                            )}
                          />
                        </>
                      );
                    }
                    const { onClick, href, target } = action;

                    return (
                      <>
                        <Button
                          className={beTwMerge(
                            baseButtonStyle,
                            '!tw-hidden lg:!tw-block tw-box-content lg:tw-w-1/2',
                            actions.length > 1
                              ? 'tw-flex-1 md:tw-max-w-[calc(50%-8px)]'
                              : 'tw-flex-1 lg:tw-flex-[0_0_calc(50%_-_0.5rem)]'
                          )}
                          key={i}
                          label={label}
                          color={color}
                          type={variant}
                          href={href}
                          target={target}
                          onClick={onClick}
                        />

                        <Button
                          className={beTwMerge(
                            baseButtonStyle,
                            '!tw-block tw-w-[calc(100%-35px)] md:tw-w-full lg:!tw-hidden tw-box-content lg:tw-w-1/2',
                            mobileVariant === 'secondary' ? 'tw-w-[calc(100%-37px)]' : '',
                            actions.length > 1
                              ? 'tw-flex-1 md:tw-max-w-[calc(50%-8px)]'
                              : 'tw-flex-1 lg:tw-flex-[0_0_calc(50%_-_0.5rem)]'
                          )}
                          key={i}
                          label={label}
                          color={mobileColor || color}
                          type={mobileVariant || variant}
                          href={href}
                          target={target}
                          onClick={onClick}
                        />
                      </>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
